import { useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState, type KeyboardEvent as ReactKeyboardEvent, type Ref } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import Dialog from '@mui/material/Dialog';
import IconButton from '@mui/material/IconButton';
import InputBase from '@mui/material/InputBase';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import ListSubheader from '@mui/material/ListSubheader';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import TrackChangesOutlinedIcon from '@mui/icons-material/TrackChangesOutlined';
import RocketLaunchOutlinedIcon from '@mui/icons-material/RocketLaunchOutlined';
import FlagOutlinedIcon from '@mui/icons-material/FlagOutlined';
import GppMaybeOutlinedIcon from '@mui/icons-material/GppMaybeOutlined';
import PlaylistAddCheckOutlinedIcon from '@mui/icons-material/PlaylistAddCheckOutlined';
import GavelOutlinedIcon from '@mui/icons-material/GavelOutlined';
import EventNoteOutlinedIcon from '@mui/icons-material/EventNoteOutlined';
import LibraryBooksOutlinedIcon from '@mui/icons-material/LibraryBooksOutlined';
import FolderCopyOutlinedIcon from '@mui/icons-material/FolderCopyOutlined';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import EditNoteOutlinedIcon from '@mui/icons-material/EditNoteOutlined';
import type { SvgIconComponent } from '@mui/icons-material';
import { SEARCH_MIN_LENGTH, useGlobalSearch, type SearchHit, type SearchHitType } from '@/api/search';

/** The same icons the navigation uses for each area, so a hit reads as the page it opens. */
const ICONS: Record<SearchHitType, SvgIconComponent> = {
  kpi: TrackChangesOutlinedIcon,
  initiative: RocketLaunchOutlinedIcon,
  milestone: FlagOutlinedIcon,
  risk: GppMaybeOutlinedIcon,
  action: PlaylistAddCheckOutlinedIcon,
  decision: GavelOutlinedIcon,
  meeting: EventNoteOutlinedIcon,
  boardpack: LibraryBooksOutlinedIcon,
  document: FolderCopyOutlinedIcon,
  strategy: AccountTreeOutlinedIcon,
  person: PersonOutlineOutlinedIcon,
  note: EditNoteOutlinedIcon,
};

const RECENT_KEY = 'annova.search.recent';
const RECENT_MAX = 6;
const DEBOUNCE_MS = 250;

// Recent terms are a per-browser convenience, so browser storage is the right place; it may be
// unavailable (private window, blocked site data), in which case there simply are none.
function readRecent(): string[] {
  try {
    const raw = localStorage.getItem(RECENT_KEY);
    const list = raw ? (JSON.parse(raw) as unknown) : [];
    return Array.isArray(list) ? list.filter((x): x is string => typeof x === 'string').slice(0, RECENT_MAX) : [];
  } catch {
    return [];
  }
}

function pushRecent(term: string): string[] {
  const next = [term, ...readRecent().filter((t) => t.toLowerCase() !== term.toLowerCase())].slice(0, RECENT_MAX);
  try { localStorage.setItem(RECENT_KEY, JSON.stringify(next)); } catch { /* per-viewer convenience only */ }
  return next;
}

/** One row the keyboard can land on: a hit, a "see all" link for a group, or a recent term. */
type Row =
  | { kind: 'hit'; hit: SearchHit }
  | { kind: 'more'; label: string; route: string }
  | { kind: 'recent'; term: string };

/**
 * The search box at the top of the application. Types into one query across everything the person
 * may see, shows a few of each kind under the box, and opens the chosen record. Ctrl+K (⌘K on a Mac)
 * or "/" focuses it from anywhere; on a narrow screen it is a magnifier that opens a full-screen
 * dialog, because the bar itself has no room there.
 *
 * Which kinds appear is the API's decision from the caller's permissions; nothing here filters by
 * permission, so the box cannot show what the page it opens would refuse.
 */
export default function GlobalSearch() {
  const theme = useTheme();
  const narrow = useMediaQuery(theme.breakpoints.down('md'));
  const [dialogOpen, setDialogOpen] = useState(false);
  const barRef = useRef<SearchPanelHandle>(null);

  // Ctrl+K / ⌘K anywhere, and "/" when nothing else has the keyboard: the shortcuts people expect.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const typing = (e.target as HTMLElement | null)?.closest('input, textarea, [contenteditable="true"]');
      const chord = (e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k';
      if (!chord && !(e.key === '/' && !typing)) return;
      e.preventDefault();
      if (narrow) setDialogOpen(true);
      else barRef.current?.focus();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [narrow]);

  if (narrow) {
    return (
      <>
        <Tooltip title="Search (Ctrl+K)">
          <IconButton color="inherit" onClick={() => setDialogOpen(true)} aria-label="Search" sx={{ ml: 'auto' }}>
            <SearchIcon />
          </IconButton>
        </Tooltip>
        <Dialog fullScreen open={dialogOpen} onClose={() => setDialogOpen(false)}>
          <Box sx={{ p: 1.5 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <SearchPanel variant="dialog" onNavigated={() => setDialogOpen(false)} />
              <IconButton onClick={() => setDialogOpen(false)} aria-label="Close search"><CloseIcon /></IconButton>
            </Box>
          </Box>
        </Dialog>
      </>
    );
  }

  return <SearchPanel ref={barRef} variant="bar" />;
}

interface SearchPanelHandle { focus: () => void }

interface SearchPanelProps {
  /** The bar sits in the header and drops its results below; the dialog is the whole screen and lists them inline. */
  variant: 'bar' | 'dialog';
  onNavigated?: () => void;
  ref?: Ref<SearchPanelHandle>;
}

function SearchPanel({ variant, onNavigated, ref }: SearchPanelProps) {
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const anchorRef = useRef<HTMLDivElement>(null);
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const [recent, setRecent] = useState<string[]>(() => readRecent());

  useImperativeHandle(ref, () => ({
    focus: () => { inputRef.current?.focus(); inputRef.current?.select(); setOpen(true); },
  }), []);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(term), DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [term]);

  const { data, isFetching } = useGlobalSearch(debounced);
  const searching = term.trim().length >= SEARCH_MIN_LENGTH;
  // The answer on screen is for the debounced term; while the typed one is newer, say so rather than
  // show stale rows as if they were current.
  const pending = searching && (isFetching || debounced.trim() !== term.trim());

  // Everything the keyboard can reach, in the order it is drawn.
  const rows = useMemo<Row[]>(() => {
    if (!searching) return recent.map((t) => ({ kind: 'recent', term: t }));
    const out: Row[] = [];
    for (const g of data?.groups ?? []) {
      for (const hit of g.hits) out.push({ kind: 'hit', hit });
      if (g.hasMore && g.moreRoute) out.push({ kind: 'more', label: `See all ${g.label.toLowerCase()}`, route: g.moreRoute });
    }
    return out;
  }, [searching, data, recent]);

  useEffect(() => { setActive(0); }, [rows.length, debounced]);

  const close = useCallback(() => { setOpen(false); inputRef.current?.blur(); }, []);

  const go = useCallback((row: Row) => {
    if (row.kind === 'recent') { setTerm(row.term); setOpen(true); inputRef.current?.focus(); return; }
    const remembered = term.trim();
    if (remembered) setRecent(pushRecent(remembered));
    setTerm('');
    setDebounced('');
    close();
    onNavigated?.();
    navigate(row.kind === 'hit' ? row.hit.route : row.route);
  }, [term, close, onNavigated, navigate]);

  const onKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Escape') { e.preventDefault(); if (term) setTerm(''); else close(); return; }
    if (rows.length === 0) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setOpen(true); setActive((i) => (i + 1) % rows.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActive((i) => (i - 1 + rows.length) % rows.length); }
    else if (e.key === 'Enter') { e.preventDefault(); go(rows[Math.min(active, rows.length - 1)]); }
  };

  const box = (
    <Box
      ref={anchorRef}
      sx={variant === 'bar' ? {
        display: 'flex', alignItems: 'center', gap: 1, mx: 'auto', width: '100%', maxWidth: 520, px: 1.5, height: 34, borderRadius: 1,
        color: 'inherit',
        bgcolor: (t) => t.palette.chrome.searchBackground,
        '&:hover, &:focus-within': { bgcolor: (t) => t.palette.chrome.searchHoverBackground },
        transition: 'background-color .15s',
      } : { display: 'flex', alignItems: 'center', gap: 1, flexGrow: 1, px: 1.5, height: 40, borderRadius: 1, bgcolor: 'action.hover' }}
    >
      <SearchIcon fontSize="small" sx={{ opacity: 0.85 }} />
      <InputBase
        inputRef={inputRef}
        value={term}
        onChange={(e) => { setTerm(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKeyDown}
        autoFocus={variant === 'dialog'}
        placeholder="Search"
        inputProps={{ 'aria-label': 'Search', 'aria-expanded': open, 'aria-controls': 'global-search-results', autoComplete: 'off' }}
        sx={{ color: 'inherit', flexGrow: 1, fontSize: 14, '& input::placeholder': { color: 'inherit', opacity: 0.85 } }}
      />
      {pending
        ? <CircularProgress size={14} color="inherit" sx={{ opacity: 0.8 }} />
        : variant === 'bar' && !term && (
          <Typography variant="caption" sx={{ opacity: 0.7, display: { xs: 'none', lg: 'block' }, whiteSpace: 'nowrap' }}>Ctrl+K</Typography>
        )}
    </Box>
  );

  const showPanel = open && (rows.length > 0 || searching);
  const results = showPanel && (
    <ResultsList rows={rows} active={active} setActive={setActive} go={go} searching={searching} pending={pending}
      empty={searching && !pending && (data?.groups.length ?? 0) === 0} />
  );

  if (variant === 'dialog') {
    return (
      <Box sx={{ flexGrow: 1 }}>
        {box}
        <Box sx={{ mt: 1 }}>{results}</Box>
      </Box>
    );
  }

  return (
    <ClickAwayListener onClickAway={() => setOpen(false)}>
      <Box sx={{ mx: 'auto', width: '100%', maxWidth: 520 }}>
        {box}
        <Popper open={Boolean(showPanel)} anchorEl={anchorRef.current} placement="bottom-start" sx={{ zIndex: (t) => t.zIndex.modal }}
          modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}>
          <Paper elevation={6} sx={{ width: anchorRef.current?.clientWidth ?? 520, maxHeight: '70vh', overflowY: 'auto', color: 'text.primary' }}>
            {results}
          </Paper>
        </Popper>
      </Box>
    </ClickAwayListener>
  );
}

function ResultsList({ rows, active, setActive, go, searching, pending, empty }: {
  rows: Row[]; active: number; setActive: (i: number) => void; go: (row: Row) => void; searching: boolean; pending: boolean; empty: boolean;
}) {
  if (empty) {
    return <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>Nothing matches. Codes, names, titles, owners and document titles are searched.</Typography>;
  }
  if (!searching && rows.length === 0) return null;
  if (rows.length === 0) {
    return pending ? <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>Searching…</Typography> : null;
  }

  // Draw a subheader whenever the kind changes; "recent" is its own kind.
  let lastGroup: string | null = null;
  return (
    <List dense disablePadding id="global-search-results" role="listbox" sx={{ py: 0.5 }}>
      {rows.map((row, i) => {
        const group = row.kind === 'recent' ? 'Recent searches' : row.kind === 'hit' ? row.hit.type : lastGroup;
        const header = group !== lastGroup && row.kind !== 'more' ? group : null;
        lastGroup = group;
        const Icon = row.kind === 'hit' ? ICONS[row.hit.type] : row.kind === 'recent' ? HistoryOutlinedIcon : null;
        return (
          <Box key={row.kind === 'hit' ? `${row.hit.type}:${row.hit.id}` : row.kind === 'more' ? `more:${row.route}` : `recent:${row.term}`}>
            {header && row.kind === 'hit' && <ListSubheader disableSticky sx={{ lineHeight: '28px', textTransform: 'capitalize' }}>{labelFor(row.hit.type)}</ListSubheader>}
            {header && row.kind === 'recent' && <ListSubheader disableSticky sx={{ lineHeight: '28px' }}>Recent searches</ListSubheader>}
            <ListItemButton
              role="option" aria-selected={i === active} selected={i === active}
              onMouseEnter={() => setActive(i)} onMouseDown={(e) => e.preventDefault()} onClick={() => go(row)}
              sx={row.kind === 'more' ? { pl: 7, py: 0.25 } : { py: 0.5 }}
            >
              {Icon && <ListItemIcon sx={{ minWidth: 36 }}><Icon fontSize="small" /></ListItemIcon>}
              {row.kind === 'hit' && (
                <ListItemText
                  primary={<Box component="span" sx={{ display: 'flex', gap: 1, alignItems: 'baseline' }}>
                    {row.hit.code && <Typography component="span" variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{row.hit.code}</Typography>}
                    <Typography component="span" variant="body2" noWrap>{row.hit.title}</Typography>
                  </Box>}
                  secondary={row.hit.subtitle ? <Typography component="span" variant="caption" color="text.secondary" noWrap display="block">{row.hit.subtitle}</Typography> : undefined}
                  disableTypography
                />
              )}
              {row.kind === 'more' && <ListItemText disableTypography primary={<Typography variant="caption" color="primary">{row.label} →</Typography>} />}
              {row.kind === 'recent' && <ListItemText disableTypography primary={<Typography variant="body2">{row.term}</Typography>} />}
            </ListItemButton>
          </Box>
        );
      })}
    </List>
  );
}

function labelFor(type: SearchHitType): string {
  switch (type) {
    case 'kpi': return 'KPIs';
    case 'initiative': return 'Initiatives and projects';
    case 'milestone': return 'Milestones';
    case 'risk': return 'Risks';
    case 'action': return 'Actions';
    case 'decision': return 'Decisions';
    case 'meeting': return 'Meetings';
    case 'boardpack': return 'Board packs';
    case 'document': return 'Documents';
    case 'strategy': return 'Strategy';
    case 'person': return 'People';
    case 'note': return 'Notes on my board packs';
  }
}
